/**
 * Compact 👍/👎 feedback row. Logs against a brief or a scam_check so the
 * team can see which corpus items are pulling their weight.
 *
 * Usage:
 *   <FeedbackRow targetKind="brief" targetId={journey.journey_id} corpusItemIds={journey.brief_corpus_ids} />
 */
import { useState } from "react";
import axios from "axios";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function FeedbackRow({ targetKind, targetId, corpusItemIds = [], className = "" }) {
  const [state, setState] = useState({ verdict: null, submitting: false });

  if (!targetId) return null;

  const submit = async (v) => {
    if (state.submitting || state.verdict) return;
    setState({ verdict: null, submitting: true });
    try {
      await axios.post(`${API}/navi/feedback`, {
        target_kind: targetKind,
        target_id: targetId,
        verdict: v,
        corpus_item_ids: corpusItemIds || [],
      });
      setState({ verdict: v, submitting: false });
    } catch {
      setState({ verdict: null, submitting: false });
    }
  };

  if (state.verdict) {
    return (
      <div className={`flex items-center gap-1.5 text-[11px] text-slate-500 ${className}`} data-testid={`feedback-thanks-${targetKind}`}>
        <Check className="w-3.5 h-3.5 text-emerald-500" />
        Thanks — feedback logged {corpusItemIds.length > 0 ? `against ${corpusItemIds.length} corpus item${corpusItemIds.length === 1 ? "" : "s"}` : ""}.
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 text-[11px] text-slate-500 ${className}`} data-testid={`feedback-row-${targetKind}`}>
      <span>Was this helpful?</span>
      <button
        onClick={() => submit("helpful")}
        disabled={state.submitting}
        data-testid={`feedback-up-${targetKind}`}
        className="rounded-full p-1.5 hover:bg-emerald-50 hover:text-emerald-700 transition disabled:opacity-40"
        aria-label="Helpful"
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => submit("not_helpful")}
        disabled={state.submitting}
        data-testid={`feedback-down-${targetKind}`}
        className="rounded-full p-1.5 hover:bg-rose-50 hover:text-rose-700 transition disabled:opacity-40"
        aria-label="Not helpful"
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
